/**
 * EngTaxonomy — 資産カテゴリ（業務用）と 品番グループ（採番用）の設定。
 * 品番グループは採番のためだけの概念で、既存 SKU（ELC00012 等）は一切変更しない。
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useOutletContext } from 'react-router-dom';
import { Tag, Hash, Plus, PencilSimple, Trash, ArrowsClockwise, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { engApi, CATEGORY_BADGE, CATEGORY_DOT, FLOW_TYPE_LABEL } from './engApi';
import { EngUnknownSkuDialog } from './EngUnknownSkuDialog';
import { useAuth } from '@/auth/AuthContext';

const COLORS = ['blue', 'orange', 'purple', 'indigo', 'green', 'gray'];
const FLOWS = ['component', 'consumable', 'equipment', 'output'];
const GROUP_CODE_RE = /^[A-Z]{2,5}$/;
const emptyCat = () => ({ id: '', name_ja: '', color: 'gray', flow_type: 'component', sort_order: 100 });
// 既定カテゴリは 部品（component）から始める — 空のまま作ると「番号は出るのに
// カテゴリが無い」グループになり、登録画面で詰まる。
const emptyGroup = () => ({ code: '', name_ja: '', default_category_id: 'component', sort_order: 100 });

export default function EngTaxonomy() {
  const ctx = useOutletContext?.() || {};
  const { user } = useAuth();
  const access = ctx.access || { is_member: false, is_admin: false };
  // 設定画面（/settings/taxonomy）には技術のアウトレットコンテキストが無いので、
  // 以前はここが false になり、未登録プレフィックスのチップが押せなかった。
  const canEdit = access.is_admin || access.is_member
    || ['admin', 'manager'].includes(user?.role);
  const [cats, setCats] = useState([]);
  const [groups, setGroups] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [catDraft, setCatDraft] = useState(null);
  const [grpDraft, setGrpDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, g, o] = await Promise.all([
        engApi.assetCategories(), engApi.skuGroups(), engApi.unregisteredSkuPrefixes()]);
      setCats(c || []); setGroups(g || []); setOrphans(o || []);
    } catch (_e) { toast.error('マスタの取得に失敗しました'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCat = async () => {
    if (!(catDraft.name_ja || '').trim()) { toast.error('カテゴリ名を入力してください'); return; }
    setSaving(true);
    try {
      const body = { name_ja: catDraft.name_ja.trim(), color: catDraft.color,
        flow_type: catDraft.flow_type, sort_order: Number(catDraft.sort_order) || 100 };
      if (catDraft.id) await engApi.updateAssetCategory(catDraft.id, body);
      else await engApi.createAssetCategory(body);
      toast.success('保存しました'); setCatDraft(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const removeCat = async (c) => {
    if (!window.confirm(`カテゴリ「${c.name_ja}」を削除しますか？`)) return;
    try { await engApi.deleteAssetCategory(c.id); toast.success('削除しました'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  const saveGroup = async () => {
    const code = (grpDraft.code || '').trim().toUpperCase();
    if (!GROUP_CODE_RE.test(code)) { toast.error('コードは英字2〜5文字で入力してください'); return; }
    if (!(grpDraft.name_ja || '').trim()) { toast.error('名称を入力してください'); return; }
    setSaving(true);
    try {
      const body = { code, name_ja: grpDraft.name_ja.trim(),
        default_category_id: grpDraft.default_category_id || null,
        sort_order: Number(grpDraft.sort_order) || 100 };
      if (grpDraft.existing) await engApi.updateSkuGroup(code, body);
      else await engApi.createSkuGroup(body);
      toast.success('保存しました'); setGrpDraft(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const removeGroup = async (g) => {
    if (!window.confirm(`品番グループ「${g.code}」を削除しますか？（既存の品番は変わりません）`)) return;
    try { await engApi.deleteSkuGroup(g.code); toast.success('削除しました'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  const mapCategory = async (g, categoryId) => {
    try {
      await engApi.updateSkuGroup(g.code, { code: g.code, name_ja: g.name_ja,
        default_category_id: categoryId || null, sort_order: g.sort_order ?? 100 });
      toast.success(`${g.code} の既定カテゴリを変更しました`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
  };

  const classifyUnclassified = async () => {
    try {
      const r = await engApi.reclassifyTaxonomy();
      toast.success(r.assets_classified
        ? `未分類 ${r.assets_classified}件 を分類しました`
        : '分類できる資産はありません（未登録品番を検出して割り当ててください）');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '分類に失敗しました'); }
  };

  return (
    <div className="space-y-6" data-testid="eng-taxonomy-page">
      <div>
        <div className="flex items-center gap-2">
          <Tag size={20} weight="duotone" className="text-teal-600" />
          <h1 className="text-lg font-bold text-zinc-900">カテゴリ・品番グループ</h1>
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          <span className="font-semibold">品番グループ</span>は採番専用（既存の品番は変わりません）。
          <span className="font-semibold"> 資産カテゴリ</span>は在庫の見え方・フィルタ・操作ルールを決めます。
        </p>
      </div>

      {/* 資産カテゴリ */}
      <section className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-bold text-zinc-800">資産カテゴリ</h2>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setCatDraft(emptyCat())} data-testid="eng-tax-cat-add">
              <Plus size={14} className="mr-1" /> カテゴリを追加
            </Button>
          )}
        </div>
        <div className="divide-y divide-zinc-200" data-testid="eng-tax-cat-list">
          {cats.map((c) => (
            <div key={c.id} className="flex items-center gap-3 py-2.5" data-testid={`eng-tax-cat-${c.id}`}>
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${CATEGORY_BADGE[c.color] || CATEGORY_BADGE.gray}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${CATEGORY_DOT[c.color] || CATEGORY_DOT.gray}`} />
                {c.name_ja}
              </span>
              <span className="flex-1 min-w-0 text-[12px] text-zinc-500 truncate">{FLOW_TYPE_LABEL[c.flow_type] || c.flow_type}</span>
              {/* 登録先の台帳は API が返す値をそのまま出す（画面側に台帳名を持たない）。
                  20260610 · 持ち主は flow_type から導出されるので、管理者が作った
                  カテゴリにも必ず値が入る。 */}
              {c.owner_label && (
                <span
                  className={`text-[10px] font-semibold rounded px-1.5 py-0.5 border whitespace-nowrap ${
                    c.foreign_ledger
                      ? 'bg-purple-50 text-purple-700 border-purple-200'
                      : 'bg-zinc-50 text-zinc-500 border-zinc-200'}`}
                  title={c.owner_screen || ''}
                  data-testid={`eng-tax-cat-owner-${c.id}`}
                >
                  {c.owner_label}
                </span>
              )}
              {c.selectable === false && (
                <span className="text-[10px] font-semibold text-amber-600" data-testid={`eng-tax-cat-fallback-${c.id}`}>移行専用</span>
              )}
              {c.is_system && <span className="text-[10px] font-semibold text-zinc-400">標準</span>}
              {canEdit && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setCatDraft({ ...c })} className="p-1 text-zinc-400 hover:text-teal-700"
                    title="編集" data-testid={`eng-tax-cat-edit-${c.id}`}><PencilSimple size={14} /></button>
                  {!c.is_system && (
                    <button onClick={() => removeCat(c)} className="p-1 text-zinc-400 hover:text-rose-600"
                      title="削除" data-testid={`eng-tax-cat-delete-${c.id}`}><Trash size={14} /></button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* 品番グループ → 既定カテゴリ */}
      <section className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Hash size={17} weight="bold" className="text-zinc-400" />
            <h2 className="text-base font-bold text-zinc-800">品番グループ → デフォルトカテゴリ</h2>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setDiscoverOpen(true)} data-testid="eng-tax-discover">
                <MagnifyingGlass size={14} className="mr-1" /> 未登録品番を検出
              </Button>
              <Button size="sm" variant="outline" onClick={classifyUnclassified} data-testid="eng-tax-reclassify">
                <ArrowsClockwise size={14} className="mr-1" /> 未分類資産を分類
              </Button>
              <Button size="sm" variant="outline" onClick={() => setGrpDraft(emptyGroup())} data-testid="eng-tax-group-add">
                <Plus size={14} className="mr-1" /> 品番グループを追加
              </Button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="eng-tax-group-table">
            <thead className="bg-zinc-100 text-zinc-700 text-[11px] uppercase">
              <tr>
                <th className="text-left px-3 py-2 font-semibold w-24">グループ</th>
                <th className="text-left px-3 py-2 font-semibold">名称</th>
                <th className="text-left px-3 py-2 font-semibold w-56">デフォルトカテゴリ</th>
                <th className="text-right px-3 py-2 font-semibold w-20">資産数</th>
                <th className="w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {groups.map((g) => (
                <tr key={g.code} data-testid={`eng-tax-group-${g.code}`}>
                  <td className="px-3 py-2 font-mono font-bold text-teal-700">{g.code}</td>
                  <td className="px-3 py-2 text-zinc-700">{g.name_ja}</td>
                  <td className="px-3 py-2">
                    <select value={g.default_category_id || ''} disabled={!canEdit}
                      onChange={(e) => mapCategory(g, e.target.value)}
                      className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm disabled:bg-zinc-50"
                      data-testid={`eng-tax-group-cat-${g.code}`}>
                      <option value="">（未設定）</option>
                      {cats.filter((c) => c.selectable !== false || c.id === g.default_category_id)
                        .map((c) => <option key={c.id} value={c.id}>{c.name_ja}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-600">{g.asset_count ?? 0}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canEdit && (
                      <>
                        <button onClick={() => setGrpDraft({ ...g, existing: true })} className="p-1 text-zinc-400 hover:text-teal-700"
                          title="編集" data-testid={`eng-tax-group-edit-${g.code}`}><PencilSimple size={14} /></button>
                        {!g.is_system && (
                          <button onClick={() => removeGroup(g)} className="p-1 text-zinc-400 hover:text-rose-600"
                            title="削除" data-testid={`eng-tax-group-delete-${g.code}`}><Trash size={14} /></button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-zinc-400">
          資産登録時、選んだ品番グループの既定カテゴリが自動でセットされます（登録者が変更可能）。
        </p>

        {orphans.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="eng-tax-orphans">
            <p className="text-[12px] font-semibold text-amber-800">
              マスタ未登録の品番プレフィックス（全モジュール）</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {orphans.map((o) => (
                <button key={o.code} disabled={!canEdit}
                  onClick={() => setGrpDraft({ ...emptyGroup(), code: o.code, name_ja: '',
                    default_category_id: o.suggested_category_id || 'component',
                    code_locked: true })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-mono font-bold text-amber-800 hover:bg-amber-100 disabled:opacity-60"
                  data-testid={`eng-tax-orphan-${o.code}`}>
                  {o.code}
                  <span className="font-sans font-normal text-amber-600">{o.total_count ?? o.asset_count}件</span>
                  {canEdit && <Plus size={11} weight="bold" />}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-amber-700">クリックすると品番グループとして登録できます（既存の品番は変わりません）。</p>
          </div>
        )}
      </section>

      <EngUnknownSkuDialog open={discoverOpen} onClose={() => setDiscoverOpen(false)}
        cats={cats} onApplied={load} />

      {/* カテゴリ編集 */}
      <Dialog open={!!catDraft} onOpenChange={(o) => !o && setCatDraft(null)}>
        <DialogContent aria-describedby={undefined} data-testid="eng-tax-cat-dialog">
          <DialogHeader><DialogTitle>{catDraft?.id ? 'カテゴリを編集' : 'カテゴリを追加'}</DialogTitle></DialogHeader>
          {catDraft && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">カテゴリ名 <span className="text-rose-500">＊</span></Label>
                <Input value={catDraft.name_ja} onChange={(e) => setCatDraft((d) => ({ ...d, name_ja: e.target.value }))}
                  placeholder="例: 治具" className="mt-1" data-testid="eng-tax-cat-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">色</Label>
                  <select value={catDraft.color} onChange={(e) => setCatDraft((d) => ({ ...d, color: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm" data-testid="eng-tax-cat-color">
                    {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">表示順</Label>
                  <Input type="number" value={catDraft.sort_order}
                    onChange={(e) => setCatDraft((d) => ({ ...d, sort_order: e.target.value }))}
                    className="mt-1 tabular-nums" data-testid="eng-tax-cat-order" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">業務ルール</Label>
                <select value={catDraft.flow_type} onChange={(e) => setCatDraft((d) => ({ ...d, flow_type: e.target.value }))}
                  className="mt-1 h-10 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm" data-testid="eng-tax-cat-flow">
                  {FLOWS.map((f) => <option key={f} value={f}>{FLOW_TYPE_LABEL[f]}</option>)}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDraft(null)}>キャンセル</Button>
            <Button onClick={saveCat} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="eng-tax-cat-save">
              {saving ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 品番グループ編集 */}
      <Dialog open={!!grpDraft} onOpenChange={(o) => !o && setGrpDraft(null)}>
        <DialogContent aria-describedby={undefined} data-testid="eng-tax-group-dialog">
          <DialogHeader><DialogTitle>{grpDraft?.existing ? '品番グループを編集' : '品番グループを追加'}</DialogTitle></DialogHeader>
          {grpDraft && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">コード <span className="text-rose-500">＊</span></Label>
                  <Input value={grpDraft.code} disabled={!!grpDraft.existing || !!grpDraft.code_locked}
                    onChange={(e) => setGrpDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
                    placeholder="例: JIG" className="mt-1 font-mono uppercase disabled:bg-zinc-100" data-testid="eng-tax-group-code" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs font-semibold text-zinc-600">名称 <span className="text-rose-500">＊</span></Label>
                  <Input value={grpDraft.name_ja} onChange={(e) => setGrpDraft((d) => ({ ...d, name_ja: e.target.value }))}
                    placeholder="例: 治具" className="mt-1" data-testid="eng-tax-group-name" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">デフォルトカテゴリ</Label>
                <select value={grpDraft.default_category_id || ''}
                  onChange={(e) => setGrpDraft((d) => ({ ...d, default_category_id: e.target.value }))}
                  className="mt-1 h-10 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm" data-testid="eng-tax-group-defcat">
                  <option value="">（未設定）</option>
                  {cats.filter((c) => c.selectable !== false || c.id === grpDraft.default_category_id)
                    .map((c) => <option key={c.id} value={c.id}>{c.name_ja}</option>)}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrpDraft(null)}>キャンセル</Button>
            <Button onClick={saveGroup} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="eng-tax-group-save">
              {saving ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
